import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { apiClient } from '@/lib/apiClient'

interface Vertical {
  code: string
  displayName: string
  description: string | null
}

interface DashboardTile {
  tileKey: string
  displayName: string
  displayType: string
  verticalCode: string
  dataEndpoint: string | null
  sortOrder: number
}

export function VerticalSettingsPage() {
  const { t } = useTranslation()
  const [verticals, setVerticals] = useState<Vertical[]>([])
  const [tiles, setTiles] = useState<DashboardTile[]>([])
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  function flash(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3000)
  }

  const load = useCallback(async () => {
    try {
      const [vRes, tRes] = await Promise.all([
        apiClient.get<Vertical[]>('/verticals'),
        apiClient.get<DashboardTile[]>('/verticals/tiles'),
      ])
      setVerticals(vRes.data)
      setTiles(tRes.data)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const selectVertical = async (code: string) => {
    setSaving(true)
    try {
      await apiClient.put('/verticals/tenant-selection', { verticalCode: code })
      flash(t('vertical.saved'))
      const tRes = await apiClient.get<DashboardTile[]>('/verticals/tiles')
      setTiles(tRes.data)
    } catch {
      flash(t('vertical.error_save'))
    } finally {
      setSaving(false)
    }
  }

  if (loadError) {
    return <p className="text-red-600 p-6">{t('vertical.error_load')}</p>
  }

  return (
    <div className="space-y-8 p-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      <div>
        <h1 className="text-2xl font-semibold">{t('vertical.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('vertical.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {verticals.map(v => (
          <div
            key={v.code}
            className="border rounded-lg p-4 bg-card shadow-sm space-y-2"
          >
            <h3 className="font-medium">
              {t(`vertical.verticals.${v.code}` as Parameters<typeof t>[0], {
                defaultValue: v.displayName,
              })}
            </h3>
            {v.description && (
              <p className="text-xs text-muted-foreground">{v.description}</p>
            )}
            <button
              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={saving}
              onClick={() => void selectVertical(v.code)}
            >
              {saving ? t('vertical.saving') : t('vertical.select')}
            </button>
          </div>
        ))}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">{t('vertical.tiles_title')}</h2>
        {tiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('vertical.tiles_empty')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {tiles.map(tile => (
              <div
                key={tile.tileKey}
                className="border rounded-lg p-4 bg-card shadow-sm space-y-1"
              >
                <p className="text-xs font-medium text-foreground">{tile.displayName}</p>
                <p className="text-xs text-muted-foreground">{tile.displayType}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
